import { useEffect, useRef } from 'react';
import { HOVER_TARGET_SELECTOR } from './fxConstants';

/**
 * Manga-style white-gloved hand cursor.
 * - Position: pointermove → RAF → CSS variables (no React state).
 * - State: idle | hover | press, driven by data-state attribute.
 *   * hover detected via document mouseover targeting HOVER_TARGET_SELECTOR
 *   * press detected via document pointerdown / pointerup
 */
export default function CursorOverlay() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let rafId = 0;
    let nextX = -100;
    let nextY = -100;
    let scheduled = false;

    const flush = () => {
      scheduled = false;
      root.style.setProperty('--cursor-x', `${nextX}px`);
      root.style.setProperty('--cursor-y', `${nextY}px`);
    };

    const onMove = (e: PointerEvent) => {
      nextX = e.clientX;
      nextY = e.clientY;
      if (!scheduled) {
        scheduled = true;
        rafId = requestAnimationFrame(flush);
      }
    };

    const onOver = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const isHover = !!target?.closest?.(HOVER_TARGET_SELECTOR);
      if (root.dataset.state === 'press') return; // press wins over hover
      root.dataset.state = isHover ? 'hover' : 'idle';
    };

    const onDown = () => { root.dataset.state = 'press'; };
    const onUp = (e: PointerEvent) => {
      const target = e.target as Element | null;
      const isHover = !!target?.closest?.(HOVER_TARGET_SELECTOR);
      root.dataset.state = isHover ? 'hover' : 'idle';
    };
    const onLeaveWindow = () => { root.dataset.state = 'idle'; };

    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('mouseover', onOver, { passive: true });
    document.addEventListener('pointerdown', onDown, { passive: true });
    document.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('blur', onLeaveWindow);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointerup', onUp);
      window.removeEventListener('blur', onLeaveWindow);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      data-testid="fx-cursor"
      data-state="idle"
      className="cursor-root"
      aria-hidden="true"
    >
      <svg className="cursor-hand" viewBox="0 0 64 80" xmlns="http://www.w3.org/2000/svg">
        {/* Idle / hover hand: index finger pointing up-left */}
        <g className="hand-base" stroke="#222" strokeWidth="3" strokeLinejoin="round" fill="#fff">
          <path d="M22 8 Q22 4 26 4 Q30 4 30 8 L30 36 L34 36 Q34 30 38 30 Q42 30 42 34 L42 40 L46 40 Q46 36 50 36 Q54 36 54 40 L54 44 L56 44 Q60 44 60 48 L60 64 Q60 76 48 76 L26 76 Q14 76 12 64 L8 50 Q6 42 14 40 Q18 40 22 46 Z" />
          <line x1="32" y1="48" x2="32" y2="60" />
          <line x1="42" y1="48" x2="42" y2="60" />
          <line x1="50" y1="50" x2="50" y2="62" />
        </g>
        {/* Press hand: all fingers curled (pinch posture) */}
        <g className="hand-press" stroke="#222" strokeWidth="3" strokeLinejoin="round" fill="#fff">
          <path d="M22 22 Q22 16 28 16 Q34 16 34 22 L34 36 L38 32 Q42 28 46 32 Q48 34 46 38 L42 44 L48 42 Q52 40 54 44 Q56 48 52 50 L48 54 L54 56 Q58 60 54 64 L48 70 Q40 78 28 74 L18 70 Q12 66 10 58 L8 50 Q6 42 14 40 Q20 40 22 46 Z" />
        </g>
      </svg>
    </div>
  );
}
